import React, { useMemo } from "react";
import { Slider, InputNumber } from "antd";
import { typography, borders, textColors, surfaces, interactive } from "../../../design-system/tokens";
import { getFontFamilyPreviewStack } from "../../../core/utils/fontFamilyUtils";

// Preset labels
const SIZE_LABELS = [
  { key: "h1", label: "H1" },
  { key: "h2", label: "H2" },
  { key: "h3", label: "H3" },
  { key: "h4", label: "H4" },
  { key: "h5", label: "H5" },
  { key: "h6", label: "H6" },
  { key: "text", label: "Text" },
];

// Default pixel sizes
const DEFAULT_FONT_SIZES = { h1: 48, h2: 42, h3: 36, h4: 30, h5: 24, h6: 20, text: 16 };

function getPreviewStack(allValues) {
  const fam = allValues?.character_style?.font_family
    || allValues?.characterStyle?.font_family
    || allValues?.font_family
    || "sans";
  return getFontFamilyPreviewStack(fam);
}

function getPreviewText(allValues) {
  if (!allValues || typeof allValues !== "object") return "Aa";
  const candidates = ["text", "title", "label", "button_text", "subtitle", "content", "description", "name"]; 
  for (const key of candidates) {
    const v = allValues[key];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v && typeof v === "object" && typeof v.content === "string" && v.content.trim()) return v.content.trim();
  }
  return "Aa";
}

export default function FontSizeField({ label, help, value, onChange, allValues, labelSize = "normal" }) {
  const numericValue = typeof value === "number" ? value : Number(value) || 16;
  const previewStack = getPreviewStack(allValues);
  const previewText = getPreviewText(allValues);

  const websiteSizes = allValues?.website_font_sizes;
  const presets = useMemo(() => {
    return SIZE_LABELS.map(({ key, label }) => {
      const px = websiteSizes && websiteSizes[key] ? websiteSizes[key] : DEFAULT_FONT_SIZES[key];
      return { key, label, px };
    });
  }, [websiteSizes]);

  const isSelectedPreset = (px) => Number(px) === Number(numericValue);

  const labelClass = labelSize === "small" 
    ? `${typography.bodySmall} font-medium ${textColors.secondary} mb-2` 
    : `block mb-2 ${typography.label} ${textColors.secondary}`;

  return (
    <div className={labelSize === "small" ? "space-y-2" : "field-wrapper"}>
      {label && <label className={labelClass}>{label}</label>}

      {/* Preset cards */}
      <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(68px, 1fr))" }}>
        {presets.map(({ key, label: presetLabel, px }) => {
          const selected = isSelectedPreset(px);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(px)}
              className={`border rounded-lg p-2 text-center transition-all ${
                selected
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                  : `${borders.default} ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
              }`}
              aria-pressed={selected}
              title={`${presetLabel} • ${px}px`}
            >
              <div
                className="truncate leading-none mx-auto"
                style={{
                  fontFamily: previewStack,
                  fontSize: `${Math.min(px, 32)}px`,
                  lineHeight: 1.2,
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {previewText.slice(0, 3)}
              </div>
              <div className={`mt-1 text-[10px] leading-tight font-medium ${selected ? "text-white/70" : textColors.muted}`}>
                {presetLabel} · {px}px
              </div>
            </button>
          );
        })}
      </div>

      {/* Slider + InputNumber row */}
      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <Slider
            value={numericValue}
            onChange={(v) => onChange(v)}
            min={1}
            max={200}
            step={1}
          />
        </div>
        <div className="w-20 flex-shrink-0">
          <InputNumber
            value={numericValue}
            min={1}
            max={200}
            step={1}
            className="w-full"
            formatter={(v) => `${v}px`}
            parser={(v) => v.replace("px", "")}
            onChange={(num) => {
              if (typeof num === "number") onChange(Math.max(1, Math.min(200, num)));
            }}
          />
        </div>
      </div>

      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}
